/**
 * Who the product is, where it lives, and the identifiers each platform knows
 * it by. Every other package reads these rather than repeating a literal: a
 * publisher name, a support URL or a Store product id written twice is a
 * publisher name, a support URL or a Store product id that will disagree.
 */
/** The canonical public origin. The website overrides it per deploy (VITE_SITE_URL). */
export const ORIGIN = 'https://transportbook.app';
/**
 * Where the web app is served (apps/web, Cloudflare Pages). Its own origin, not a
 * path on the website: the marketing site and the app deploy separately.
 */
export const WEB_APP_ORIGIN = 'https://my.transportbook.app';
export const product = {
    name: 'Transport Book',
    // phone apps' app.config only; remove with the next native release
    betaName: 'Transport Book Beta',
    publisher: 'R R Infotech',
    contactEmail: 'contact@transportbook.app',
};
/**
 * Who a customer's contract and payment are with, and where each kind of question goes, on the
 * legal pages. Every @transportbook.app address reaches the owner through Cloudflare Email Routing.
 */
export const businessDetails = {
    legalName: product.publisher,
    emails: {
        general: product.contactEmail,
        support: 'support@transportbook.app',
        billing: 'billing@transportbook.app',
        privacy: 'privacy@transportbook.app',
        grievance: 'grievance@transportbook.app',
    },
};
/** The driver's phone app (apps/driver): a separate app with its own name and package. */
export const driverProduct = {
    name: 'Transport Book Driver',
    // phone apps' app.config only; remove with the next native release
    betaName: 'Transport Book Driver Beta',
};
const url = (path) => `${ORIGIN}${path}`;
/** Public URLs. Store listings, in-app links and the website all point at these. */
export const urls = {
    website: ORIGIN,
    privacy: url('/privacy'),
    terms: url('/terms'),
    cookies: url('/cookies'),
    refunds: url('/refunds'),
    delivery: url('/delivery'),
    contact: url('/contact'),
    support: url('/support'),
    requestDemo: url('/request-demo'),
    /** The price band (`plans.ts`), on one page. */
    pricing: url('/pricing'),
    /** Where the payment page sends a person back to. */
    paymentComplete: url('/payment-complete'),
    /** Self-serve free trial: an organisation and its administrator, with nobody approving it. */
    startTrial: url('/start-trial'),
    /** Forgot-password page: a phone OTP sets a new password for any Transport Book account. */
    resetPassword: url('/reset-password'),
    desktopDownloads: url('/downloads/desktop'),
    androidDownload: url('/downloads/android'),
    driverAndroidDownload: url('/downloads/driver'),
    /** The website's page about the web app; `webApp` is the app itself. */
    webAppPage: url('/web'),
    webApp: WEB_APP_ORIGIN,
};
/**
 * Microsoft Store. `productId` is public (it is in every store URL); the
 * Partner Center *app id* the release workflow submits with is a secret and
 * stays in GitHub secrets.
 */
export const microsoftStore = {
    productId: '9NCXNXW5SXSR',
    url: 'https://apps.microsoft.com/detail/9NCXNXW5SXSR',
    label: 'Get it from Microsoft Store',
};
/**
 * AppX/MSIX package identity, as registered in Partner Center. These must match
 * the reservation exactly or the upload is rejected; they are consumed by
 * apps/desktop/scripts/create-desktop-build-config.mjs.
 */
export const windowsPackage = {
    applicationId: 'TransportBook',
    identityName: '52892RRInfotech.RRTransportManager',
    publisher: 'CN=3953EBA3-C9C9-42FF-9B53-EE60959A8094',
    publisherDisplayName: product.publisher,
    backgroundColor: '#061A3A',
    languages: ['en-US'],
    /**
     * Partner Center rejects an MSIX whose TargetDeviceFamily MinVersion is
     * <= 10.0.17134.0, and the release workflow submits the .appx renamed to
     * .msix. 17763 is Windows 10 1809 — the same floor `systemRequirements`
     * advertises, so the manifest and the listing agree.
     */
    minVersion: '10.0.17763.0',
    maxVersionTested: '10.0.22621.0',
};
/** Android application ids. */
export const androidPackage = {
    applicationId: 'com.ravipoonia.transportmanager',
    // phone apps' app.config only; remove with the next native release
    betaApplicationId: 'com.ravipoonia.transportmanager.beta',
};
/** The driver app's Android application ids. */
export const androidDriverPackage = {
    applicationId: 'com.ravipoonia.transportbookdriver',
    // phone apps' app.config only; remove with the next native release
    betaApplicationId: 'com.ravipoonia.transportbookdriver.beta',
};
/**
 * Categories, stated once per taxonomy. They are different vocabularies for the
 * same claim, so they move together.
 */
export const categories = {
    /** Partner Center category / subcategory. */
    microsoftStore: { category: 'Business', subcategory: 'Logistics' },
    /** schema.org applicationCategory, used in the website's JSON-LD. */
    schemaOrg: 'BusinessApplication',
    /** freedesktop.org categories for the Linux AppImage. */
    linux: 'Office;Finance',
};
/**
 * What the Store listing advertises as required, and what the AppX manifest
 * enforces through `windowsPackage.minVersion`.
 */
export const systemRequirements = {
    windows: 'Windows 10 version 17763.0 or higher, x64',
    memory: '4 GB RAM (8 GB recommended)',
    disk: '1 GB free disk',
    /** Rendered as a single line for the Store's System requirements field. */
    summary: 'Windows 10 version 17763.0 or higher, x64, 4 GB RAM (8 GB recommended), 1 GB free disk',
};
/**
 * Access is licensed per organisation outside the Store, so the Store listing is Free while the
 * website publishes the price band (`plans.ts`: Free, Starter, Business, Fleet, Large fleet). The trial lengths
 * below are the server's, stated here so every surface quotes the same days.
 */
export const pricing = {
    microsoftStore: 'Free',
    storeNote: 'Free (access is licensed per organisation outside the Store)',
    /**
     * The website's self-serve trial, which starts without anyone approving it. The server grants
     * exactly this many days: keep it equal to `SELF_SERVE_TRIAL_DAYS` in
     * `@transport/contracts/contracts/organization`.
     */
    freeTrialDays: 30,
    freeTrial: 'Free for 30 days',
    /** Ask within this many days of paying and the payment comes back (the legal pages, the promise). */
    refundDays: 30,
    /**
     * What happens around a plan's end, as the server does it: keep equal to `PAID_GRACE_DAYS`,
     * `RENEWAL_REMINDER_STAGES` and `BILLING_LINK_DAYS` (`billing.test.ts` holds them together).
     */
    renewalGraceDays: 15,
    renewalReminderDays: [30, 7, 1],
    paymentLinkDays: 7,
};
/** The platforms the product ships on, as prose for listings and JSON-LD. */
export const operatingSystems = 'Windows, macOS, Android, Web browser';
//# sourceMappingURL=identity.js.map